import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function usePolicyMeta() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '';
    const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? '';

    document.title = 'Policies & Procedures | Aligned Home Care Edinburgh';

    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Read the full policies and procedures of Aligned Home Care. Care Inspectorate registered home care across Edinburgh and Scotland. Safeguarding, data protection, complaints and more.');

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://alignedhomecare.uk/policy');

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Policies & Procedures | Aligned Home Care Edinburgh');

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', 'Full policies and procedures for Aligned Home Care. Registered with the Care Inspectorate Scotland.');

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://alignedhomecare.uk/policy');

    return () => {
      document.title = prevTitle;
      if (desc) desc.setAttribute('content', prevDesc);
      if (canonical) canonical.setAttribute('href', prevCanonical);
      if (ogTitle) ogTitle.setAttribute('content', prevTitle);
      if (ogDesc) ogDesc.setAttribute('content', prevDesc);
      if (ogUrl) ogUrl.setAttribute('content', prevCanonical);
    };
  }, []);
}

const sections = [
  { id: 'safeguarding', title: 'Safeguarding & Protection of Vulnerable Adults' },
  { id: 'pvg', title: 'PVG & Background Checks' },
  { id: 'recruitment', title: 'Recruitment & Vetting' },
  { id: 'training', title: 'Staff Training & First Aid' },
  { id: 'care-planning', title: 'Care Planning & Person-Centred Care' },
  { id: 'medication', title: 'Medication Management' },
  { id: 'health-safety', title: 'Health & Safety' },
  { id: 'infection', title: 'Infection Prevention & Control' },
  { id: 'data-protection', title: 'Data Protection & Privacy' },
  { id: 'complaints', title: 'Complaints Procedure' },
  { id: 'equality', title: 'Equality, Dignity & Respect' },
  { id: 'communication', title: 'Communication & Transparency' },
  { id: 'emergency', title: 'Emergency & Incident Response' },
  { id: 'consent', title: 'Consent & Capacity' },
];

function PolicyNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-[#2C3E50]/10' : 'bg-white border-b border-[#2C3E50]/10'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/cf33c51050e95588e8f90f230098c778/2e85fd3c1c61935d17af19cdb38ff5c1.jpeg"
            alt="Aligned Home Care logo"
            className="h-10 w-10 object-contain rounded-full bg-white p-0.5 border border-[#2C3E50]/10"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-base font-bold text-[#2C3E50] tracking-tight">Aligned</span>
            <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#C0706A]">Home Care</span>
          </div>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm font-medium text-[#2C3E50] hover:text-[#C0706A] transition-colors duration-200 cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </Link>
      </div>
    </nav>
  );
}

function SectionBlock({ id, number, title, children }: { id: string; number: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mb-14 md:mb-20">
      <div className="flex items-start gap-5 mb-5">
        <span className="text-xs font-mono tracking-widest text-[#C0706A]/60 mt-1 shrink-0 w-6">{number}</span>
        <h2 className="text-xl md:text-2xl font-bold text-[#2C3E50] leading-snug">{title}</h2>
      </div>
      <div className="ml-11 text-[#2C3E50]/65 text-sm md:text-[15px] leading-relaxed space-y-4">{children}</div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C0706A] shrink-0"></span>
      <span>{children}</span>
    </li>
  );
}

function BulletList({ children }: { children: React.ReactNode }) {
  return <ul className="space-y-2">{children}</ul>;
}

export default function PolicyPage() {
  usePolicyMeta();
  const [activeSection, setActiveSection] = useState('safeguarding');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PolicyNavbar />

      <div className="bg-[#1C2B3A] pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-[#D4A574] text-xs tracking-[0.22em] uppercase font-medium mb-4">Aligned Home Care</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-5">Policies &amp; Procedures</h1>
          <p className="text-white/60 text-sm md:text-base max-w-xl leading-relaxed">
            Our commitment to safe, high-quality, person-centred care in Scotland. These policies guide everything we do and are reviewed regularly to meet the standards of the Care Inspectorate.
          </p>
          <p className="text-white/35 text-xs mt-6">Last reviewed: April 2026</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <aside className="lg:w-64 shrink-0">
          <div className="lg:sticky lg:top-28">
            <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#2C3E50]/40 mb-4">Contents</p>
            <nav className="flex flex-col gap-1">
              {sections.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`text-left text-xs leading-snug px-3 py-2 rounded-md transition-all duration-200 cursor-pointer whitespace-normal ${activeSection === s.id ? 'bg-[#C0706A]/10 text-[#C0706A] font-semibold' : 'text-[#2C3E50]/45 hover:text-[#2C3E50] hover:bg-[#2C3E50]/5'}`}
                >
                  <span className="font-mono text-[10px] text-[#C0706A]/60 mr-1.5">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="flex-1 min-w-0">

          <SectionBlock id="safeguarding" number="01" title="Safeguarding & Protection of Vulnerable Adults">
            <p>Aligned Home Care is fully committed to protecting the safety, wellbeing, and dignity of every person in our care. Safeguarding is not just a policy requirement; it is a core value that runs through everything we do.</p>
            <p>We recognise that abuse can take many forms, including:</p>
            <BulletList>
              <Bullet>Physical abuse: hitting, restraining, or causing bodily harm</Bullet>
              <Bullet>Emotional abuse: threats, humiliation, isolation, or psychological harm</Bullet>
              <Bullet>Financial abuse: theft, fraud, or misuse of a person&apos;s money or property</Bullet>
              <Bullet>Neglect: failing to meet basic needs such as food, warmth, hygiene, or medical care</Bullet>
              <Bullet>Sexual abuse: any non-consensual sexual contact or behaviour</Bullet>
            </BulletList>
            <p>Every member of staff has a personal and professional responsibility to report any concern, suspicion, or disclosure of abuse immediately to their line manager or the designated safeguarding lead. No concern is too small to raise.</p>
            <p>Where there is a risk to a person&apos;s safety, we will escalate concerns without delay to the relevant local authority, Police Scotland, or the Care Inspectorate. We work closely with multi-agency safeguarding teams and follow the Adult Support and Protection (Scotland) Act 2007 in all cases.</p>
          </SectionBlock>

          <SectionBlock id="pvg" number="02" title="PVG & Background Checks (Disclosure Scotland)">
            <p>The safety of our clients is our highest priority. All staff at Aligned Home Care are required to be members of the Protecting Vulnerable Groups (PVG) scheme, administered by Disclosure Scotland, before they begin working with clients.</p>
            <BulletList>
              <Bullet>PVG scheme membership is obtained and verified before any offer of employment is confirmed</Bullet>
              <Bullet>No member of staff is permitted to work unsupervised with clients until their PVG clearance has been received and reviewed</Bullet>
              <Bullet>We carry out ongoing suitability checks throughout employment, including monitoring for any new disclosures or concerns</Bullet>
              <Bullet>Any information disclosed through the PVG scheme is handled confidentially and assessed carefully before a decision is made</Bullet>
            </BulletList>
            <p>We take our responsibilities under the Protection of Vulnerable Groups (Scotland) Act 2007 seriously and will refer any member of staff to Disclosure Scotland if they are found to have harmed or placed a vulnerable person at risk.</p>
          </SectionBlock>

          <SectionBlock id="recruitment" number="03" title="Recruitment & Vetting">
            <p>We apply a thorough and consistent recruitment process to ensure that only suitable, trustworthy individuals join our team.</p>
            <BulletList>
              <Bullet>A minimum of two professional references are obtained and verified before employment begins</Bullet>
              <Bullet>Right to work in the UK is confirmed for all candidates in line with current legislation</Bullet>
              <Bullet>Qualifications, training certificates, and relevant experience are reviewed and verified</Bullet>
              <Bullet>All candidates complete a structured interview process designed to assess values, competence, and suitability for working with vulnerable adults</Bullet>
              <Bullet>Any gaps in employment history are explored and explained</Bullet>
              <Bullet>Health declarations are completed where required to ensure fitness for the role</Bullet>
            </BulletList>
            <p>We are committed to fair and transparent recruitment. We do not discriminate on the basis of age, gender, ethnicity, religion, disability, or any other protected characteristic.</p>
          </SectionBlock>

          <SectionBlock id="training" number="04" title="Staff Training & First Aid">
            <p>All staff at Aligned Home Care receive comprehensive training before working independently with clients, and continue to develop their skills throughout their employment.</p>
            <p>Mandatory training includes:</p>
            <BulletList>
              <Bullet>Adult safeguarding and protection of vulnerable adults</Bullet>
              <Bullet>Manual handling and moving and assisting techniques</Bullet>
              <Bullet>Infection prevention and control</Bullet>
              <Bullet>Medication awareness and safe administration</Bullet>
              <Bullet>Dementia awareness</Bullet>
              <Bullet>Mental health awareness</Bullet>
              <Bullet>Fire safety and emergency procedures</Bullet>
              <Bullet>First aid and emergency response, including CPR</Bullet>
            </BulletList>
            <p>First aid training is provided to all care staff and refreshed regularly. Staff are trained to respond calmly and effectively to medical emergencies, including falls, choking, cardiac events, and sudden deterioration in health.</p>
            <p>We support ongoing professional development and encourage staff to pursue further qualifications, including SVQ in Social Services and Healthcare.</p>
          </SectionBlock>

          <SectionBlock id="care-planning" number="05" title="Care Planning & Person-Centred Care">
            <p>Every client receives an individual care plan that is developed with them, and where appropriate, with their family or representatives. We believe that good care starts with truly understanding the person.</p>
            <BulletList>
              <Bullet>Care plans are created before care begins, based on a thorough assessment of needs, preferences, and goals</Bullet>
              <Bullet>Plans are written in plain language and shared with the client and their family</Bullet>
              <Bullet>Each plan covers personal care, health needs, social preferences, communication needs, and any specific risks</Bullet>
              <Bullet>Care plans are reviewed regularly, and any time there is a significant change in the client&apos;s condition or circumstances</Bullet>
              <Bullet>Clients and families are actively involved in reviews and their feedback is taken seriously</Bullet>
            </BulletList>
            <p>We respect that each person is an individual with their own history, values, and preferences. Our care is always shaped around the person, not a routine.</p>
          </SectionBlock>

          <SectionBlock id="medication" number="06" title="Medication Management">
            <p>Where Aligned Home Care staff are involved in supporting clients with medication, we follow strict procedures to ensure safety and accuracy.</p>
            <BulletList>
              <Bullet>Only staff who have completed medication training and been assessed as competent are permitted to administer or prompt medication</Bullet>
              <Bullet>Medication Administration Records (MAR charts) are completed accurately at every administration</Bullet>
              <Bullet>Any errors, near misses, or concerns are reported immediately and recorded</Bullet>
              <Bullet>Medication is stored securely in the client&apos;s home in line with safe storage guidelines</Bullet>
              <Bullet>We work closely with GPs, pharmacists, and district nurses to ensure medication is appropriate and up to date</Bullet>
              <Bullet>Controlled drugs are handled in line with the Misuse of Drugs Act and relevant Scottish guidance</Bullet>
            </BulletList>
            <p>Clients retain the right to manage their own medication where they have the capacity and wish to do so. We support this choice while ensuring safety is maintained.</p>
          </SectionBlock>

          <SectionBlock id="health-safety" number="07" title="Health & Safety">
            <p>Aligned Home Care takes the health and safety of both clients and staff seriously. We work to identify and reduce risks in every environment where care is delivered.</p>
            <BulletList>
              <Bullet>A risk assessment is carried out in each client&apos;s home before care begins, covering hazards such as falls, equipment, and environmental risks</Bullet>
              <Bullet>Risk assessments are reviewed regularly and updated when circumstances change</Bullet>
              <Bullet>Staff are trained in safe working practices and are expected to follow them at all times</Bullet>
              <Bullet>All accidents, incidents, and near misses are recorded and reviewed to prevent recurrence</Bullet>
              <Bullet>We comply with the Health and Safety at Work Act 1974 and all relevant Scottish regulations</Bullet>
              <Bullet>Equipment used in care delivery is regularly checked and maintained</Bullet>
            </BulletList>
          </SectionBlock>

          <SectionBlock id="infection" number="08" title="Infection Prevention & Control">
            <p>Preventing the spread of infection is a fundamental part of safe care delivery. All staff follow robust hygiene protocols at all times.</p>
            <BulletList>
              <Bullet>Staff follow strict hand hygiene procedures, washing hands before and after every care task</Bullet>
              <Bullet>Personal Protective Equipment (PPE) including gloves, aprons, and masks is used appropriately and disposed of safely</Bullet>
              <Bullet>Staff are trained in standard infection control precautions and additional precautions for specific infections</Bullet>
              <Bullet>Any staff member who is unwell must not attend work until they are symptom-free in line with current guidance</Bullet>
              <Bullet>In the event of an outbreak, we follow the guidance of Public Health Scotland and notify the Care Inspectorate as required</Bullet>
              <Bullet>Clients and families are kept informed of any infection control measures that may affect their care</Bullet>
            </BulletList>
          </SectionBlock>

          <SectionBlock id="data-protection" number="09" title="Data Protection & Privacy (UK GDPR)">
            <p>Aligned Home Care collects and uses personal information about clients, families, and staff in order to provide safe and effective care. We take our responsibilities under UK GDPR and the Data Protection Act 2018 seriously.</p>
            <BulletList>
              <Bullet>Personal data is collected only for specific, lawful purposes and is not used for anything else</Bullet>
              <Bullet>All records, whether paper or electronic, are stored securely and accessed only by those who need them</Bullet>
              <Bullet>Client information is treated as strictly confidential and is not shared without consent, except where there is a legal duty or safeguarding concern</Bullet>
              <Bullet>Clients have the right to access their own records, request corrections, and ask how their data is used</Bullet>
              <Bullet>Data is retained only for as long as necessary and disposed of securely</Bullet>
              <Bullet>Any data breach is reported to the Information Commissioner&apos;s Office (ICO) within 72 hours where required</Bullet>
            </BulletList>
            <p>If you have any questions about how we use your data, please contact us directly. We are happy to explain clearly and openly.</p>
          </SectionBlock>

          <SectionBlock id="complaints" number="10" title="Complaints Procedure">
            <p>We welcome feedback of all kinds. If something has not gone as it should, we want to know so we can put it right and improve.</p>
            <p>How to make a complaint:</p>
            <BulletList>
              <Bullet>Speak directly to your care coordinator or a senior member of staff</Bullet>
              <Bullet>Put your complaint in writing by email or letter to our registered address</Bullet>
              <Bullet>Contact us by phone during office hours</Bullet>
            </BulletList>
            <p>What happens next:</p>
            <BulletList>
              <Bullet>We will acknowledge your complaint within 3 working days</Bullet>
              <Bullet>A full investigation will be completed and a written response provided within 20 working days</Bullet>
              <Bullet>If the matter is complex, we will keep you updated on progress</Bullet>
              <Bullet>If you are not satisfied with our response, you have the right to refer your complaint to the Care Inspectorate</Bullet>
            </BulletList>
            <p>We treat all complaints seriously, confidentially, and without any negative impact on the care provided. Raising a concern will never affect the quality of service you receive.</p>
          </SectionBlock>

          <SectionBlock id="equality" number="11" title="Equality, Dignity & Respect">
            <p>Every person we support is treated with dignity, respect, and genuine kindness. We do not tolerate discrimination of any kind.</p>
            <BulletList>
              <Bullet>We provide care without discrimination on the basis of age, gender, race, religion, disability, sexual orientation, or any other characteristic</Bullet>
              <Bullet>We respect each person&apos;s cultural background, beliefs, and personal values</Bullet>
              <Bullet>We actively promote independence and support clients to make their own choices wherever possible</Bullet>
              <Bullet>Staff are trained in equality and diversity and are expected to uphold these values in every interaction</Bullet>
              <Bullet>We challenge and address any behaviour that falls short of these standards, whether from staff, clients, or visitors</Bullet>
            </BulletList>
            <p>Dignity is not just a word in a policy. It is how we speak to people, how we knock before entering a room, and how we listen when someone tells us what they need.</p>
          </SectionBlock>

          <SectionBlock id="communication" number="12" title="Communication & Transparency">
            <p>We believe that open, honest communication is the foundation of good care. Families and clients should always feel informed and heard.</p>
            <BulletList>
              <Bullet>Families receive regular updates on their loved one&apos;s wellbeing and any changes to their care</Bullet>
              <Bullet>We are clear and honest about what we can and cannot provide</Bullet>
              <Bullet>Any significant changes to a client&apos;s condition or care plan are communicated promptly</Bullet>
              <Bullet>We are accessible by phone and email during office hours, and have an out-of-hours contact for urgent matters</Bullet>
              <Bullet>We hold regular reviews with clients and families to discuss care and address any concerns</Bullet>
              <Bullet>We share information in a way that is clear and easy to understand, avoiding unnecessary jargon</Bullet>
            </BulletList>
          </SectionBlock>

          <SectionBlock id="emergency" number="13" title="Emergency & Incident Response">
            <p>We have clear procedures in place to respond quickly and effectively to any emergency or unexpected situation.</p>
            <BulletList>
              <Bullet>In the event of a medical emergency, staff will call 999 immediately and provide first aid until emergency services arrive</Bullet>
              <Bullet>The client&apos;s family and next of kin will be contacted as soon as possible</Bullet>
              <Bullet>All incidents are recorded in detail and reviewed by management</Bullet>
              <Bullet>Serious incidents are reported to the Care Inspectorate and other relevant bodies as required</Bullet>
              <Bullet>We have contingency plans in place to cover staff absence, ensuring continuity of care at all times</Bullet>
              <Bullet>Business continuity plans are reviewed regularly to ensure we can respond to unexpected disruptions</Bullet>
            </BulletList>
            <p>No client will ever be left without care due to staff absence or an unforeseen event. We have systems in place to ensure cover is always available.</p>
          </SectionBlock>

          <SectionBlock id="consent" number="14" title="Consent & Capacity">
            <p>Respecting a person&apos;s right to make their own decisions is central to everything we do. We follow the principles of the Adults with Incapacity (Scotland) Act 2000 in all situations where capacity may be a concern.</p>
            <BulletList>
              <Bullet>We always seek informed consent before carrying out any care task</Bullet>
              <Bullet>Consent is obtained in a way that is appropriate to the individual, taking into account communication needs</Bullet>
              <Bullet>We assume that a person has capacity unless there is clear evidence to the contrary</Bullet>
              <Bullet>Where a person lacks capacity, we act in their best interests and involve family, carers, and relevant professionals in decision-making</Bullet>
              <Bullet>We support clients to understand their options and make choices wherever possible, even where capacity is reduced</Bullet>
              <Bullet>Advance care plans and any legal documents such as Power of Attorney are respected and followed</Bullet>
            </BulletList>
            <p>We never carry out care without consent. If a client refuses care, we respect that decision, record it, and seek guidance where there is a concern for their safety.</p>
          </SectionBlock>

          <div className="mt-16 border-t border-[#2C3E50]/10 pt-12">
            <div className="bg-[#1C2B3A] rounded-xl px-8 py-10 md:px-12 md:py-12">
              <p className="text-[#D4A574] text-xs tracking-[0.2em] uppercase font-medium mb-4">Our Commitment</p>
              <p className="text-white text-xl md:text-2xl font-bold leading-snug mb-5">
                Safe, dignified, and genuinely caring support for every person we serve.
              </p>
              <p className="text-white/60 text-sm leading-relaxed max-w-2xl">
                These policies are not just documents. They reflect the values and standards that every member of the Aligned Home Care team is expected to uphold, every day. We review and update them regularly to ensure we continue to meet and exceed the standards set by the Care Inspectorate Scotland.
              </p>
              <p className="text-white/40 text-xs mt-6">
                For any questions about our policies, please contact us at{' '}
                <a href="mailto:info@alignedhomecare.uk" className="text-[#D4A574] hover:underline">info@alignedhomecare.uk</a>
              </p>
            </div>
          </div>

        </main>
      </div>

      <footer className="bg-[#111C26] text-white mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/35">
          <span>&copy; {new Date().getFullYear()} Aligned Home Care. All rights reserved.</span>
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
